// JarvisPage.jsx -- Jarvis Command Studio
// Key fixes in this version:
//   1. Lead titles read from leadTitle field (matches leads API / briefing.js fix)
//   2. Lead preview modal — shows real content, "Open Full Lead Card" navigates AND opens
//   3. Briefing cached in sessionStorage (30 min) — no rerun when navigating away/back
//   4. Decision Queue shows full expandable detail with no truncation
//   5. Metric cards all clickable with correct navigation targets

import React, { useState, useEffect, useCallback } from 'react';
import { useProfile } from '../context/UserProfileContext';
import './JarvisPage.css';
import { apiFetch } from '../lib/api';

// ── Jarvis Avatar ─────────────────────────────────────────────────────────────
function JarvisAvatar({ size = 40, pulse = false }) {
  return (
    <div className={`jarvis-avatar${pulse ? ' jarvis-avatar--pulse' : ''}`} style={{ width: size, height: size }}>
      <span style={{ fontSize: size * 0.42, fontWeight: 900, color: 'var(--dm-white)', letterSpacing: '-1px' }}>J</span>
    </div>
  );
}

function JarvisSays({ message, sub }) {
  const { commandName } = useProfile();
  return (
    <div className="jarvis-says">
      <JarvisAvatar size={36} />
      <div className="jarvis-says-body">
        <span className="jarvis-says-name">{commandName}</span>
        <p className="jarvis-says-msg">{message}</p>
        {sub && <p className="jarvis-says-sub">{sub}</p>}
      </div>
    </div>
  );
}

function PriorityBadge({ priority }) {
  const map = { High: 'jarvis-badge--high', Medium: 'jarvis-badge--medium', Low: 'jarvis-badge--low' };
  return <span className={`jarvis-badge ${map[priority] || map.Low}`}>{priority}</span>;
}

function ScoreRing({ score }) {
  const pct   = (score / 10) * 100;
  const color = score >= 7.5 ? 'var(--dm-green-soft)' : score >= 5 ? '#f59e0b' : 'var(--dm-grey-500)';
  const circ  = 2 * Math.PI * 18;
  return (
    <div className="jarvis-score-ring" title={`Score: ${score}/10`}>
      <svg width="44" height="44" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r="18" fill="none" stroke="#e5e7eb" strokeWidth="4" />
        <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={`${(pct / 100) * circ} ${circ}`} strokeLinecap="round" transform="rotate(-90 22 22)" />
      </svg>
      <span className="jarvis-score-number" style={{ color }}>{score}</span>
    </div>
  );
}

function MetricCard({ label, value, sub, accent, warning, onClick }) {
  return (
    <div
      className={`jarvis-metric-card jarvis-metric-card--clickable${accent ? ' jarvis-metric-card--accent' : ''}${warning ? ' jarvis-metric-card--warning' : ''}`}
      onClick={onClick}
    >
      <div className="jarvis-metric-value">{value}</div>
      <div className="jarvis-metric-label">{label}</div>
      {sub && <div className="jarvis-metric-sub">{sub}</div>}
      <div className="jarvis-metric-arrow">→</div>
    </div>
  );
}

// ── Extract display title (description part only) from "Account — Description" ──
function getDisplayTitle(rawTitle) {
  if (!rawTitle) return 'Untitled opportunity';
  if (rawTitle.includes(' — ')) return rawTitle.slice(rawTitle.indexOf(' — ') + 3).trim();
  if (rawTitle.includes(' - '))  return rawTitle.slice(rawTitle.indexOf(' - ') + 3).trim();
  return rawTitle;
}

// ── Inline Lead Preview Modal ─────────────────────────────────────────────────
// Lightweight preview inside Jarvis. "Open Full Lead Card" calls onOpenInLeads
// which navigates to LeadsPage and auto-opens the specific card.
function LeadDetailModal({ lead, onClose, onOpenInLeads }) {
  const [section, setSection] = useState('overview');
  if (!lead) return null;

  const displayTitle = getDisplayTitle(lead.leadTitle || lead.hypothesis || '');

  return (
    <div className="jarvis-lead-modal-overlay" onClick={onClose}>
      <div className="jarvis-lead-modal" onClick={e => e.stopPropagation()}>

        <div className="jarvis-lead-modal-header">
          <div className="jarvis-lead-modal-badges">
            <PriorityBadge priority={lead.priority} />
            {lead.dealSize && <span className="jarvis-lead-deal-badge">{lead.dealSize}</span>}
            <span className="jarvis-lead-score-badge">{lead.score}/10</span>
          </div>
          <button className="jarvis-lead-modal-close" onClick={onClose}>✕</button>
        </div>

        <h3 className="jarvis-lead-modal-title">{displayTitle}</h3>

        {lead.accountName && <div className="jarvis-lead-modal-account">🏢 {lead.accountName}</div>}

        {(lead.signalType || (lead.aiSignals?.length > 0)) && (
          <div className="jarvis-lead-modal-signals">
            {lead.signalType && <span className="jarvis-lead-signal-tag">{lead.signalType}</span>}
            {lead.aiSignals?.filter(s => s !== lead.signalType).map((s, i) => (
              <span key={i} className="jarvis-lead-signal-tag">{s}</span>
            ))}
          </div>
        )}

        <div className="jarvis-lead-modal-nav">
          {[
            { id: 'overview',  label: '📋 Overview' },
            { id: 'executive', label: '👤 Executive' },
            { id: 'company',   label: '🏢 Company' },
          ].map(s => (
            <button key={s.id}
              className={`jarvis-lead-modal-nav-btn${section === s.id ? ' active' : ''}`}
              onClick={() => setSection(s.id)}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="jarvis-lead-modal-body">
          {section === 'overview' && (
            <>
              {lead.opportunityHypothesis && lead.opportunityHypothesis !== (lead.leadTitle || lead.hypothesis) ? (
                <div className="jarvis-lead-modal-block">
                  <div className="jarvis-lead-modal-block-label">Opportunity Hypothesis</div>
                  <p className="jarvis-lead-modal-block-text">{lead.opportunityHypothesis}</p>
                </div>
              ) : null}
              {lead.companyOverview && (
                <div className="jarvis-lead-modal-block">
                  <div className="jarvis-lead-modal-block-label">Company Context</div>
                  <p className="jarvis-lead-modal-block-text">{lead.companyOverview}</p>
                </div>
              )}
              {!lead.opportunityHypothesis && !lead.companyOverview && (
                <p className="jarvis-lead-modal-empty">Open the full lead card to see complete intelligence.</p>
              )}
            </>
          )}
          {section === 'executive' && (
            <div className="jarvis-lead-modal-block">
              {lead.executiveTarget
                ? <><div className="jarvis-lead-modal-block-label">Target Executive</div><p className="jarvis-lead-modal-block-text">{lead.executiveTarget}</p></>
                : <p className="jarvis-lead-modal-empty">No executive intelligence yet. Run research to populate.</p>}
            </div>
          )}
          {section === 'company' && (
            <div className="jarvis-lead-modal-block">
              {lead.companyOverview
                ? <><div className="jarvis-lead-modal-block-label">Company Overview</div><p className="jarvis-lead-modal-block-text">{lead.companyOverview}</p></>
                : <p className="jarvis-lead-modal-empty">No company overview yet. Run research to populate.</p>}
            </div>
          )}
        </div>

        <div className="jarvis-lead-modal-footer">
          <button className="jarvis-btn jarvis-btn--primary" onClick={() => onOpenInLeads(lead)}>
            Open Full Lead Card →
          </button>
          <button className="jarvis-btn jarvis-btn--secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// TAB 1: Morning Briefing
// ════════════════════════════════════════════════════════════════════════════════
function MorningBriefingTab({ briefing, metrics, newLeads, urgentTasks, loading, onRefresh, onNavigate, onOpenLead, briefingRating, setBriefingRating, ratingLogged, setRatingLogged }) {
  const { firstName, commandName } = useProfile();
  const [selectedLead, setSelectedLead] = useState(null);

  if (loading) {
    return (
      <div className="jarvis-loading">
        <JarvisAvatar size={56} pulse />
        <p className="jarvis-loading-text">Victor is reviewing your pipeline...</p>
        <p className="jarvis-loading-sub">Pulling live data and building your strategic morning briefing.</p>
      </div>
    );
  }

  const hour     = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const doFirst  = (urgentTasks || []).filter(t => t.priority === 'Do First');
  const overdue  = (urgentTasks || []).filter(t => t.overdue);

  return (
    <div className="jarvis-tab-content">

      <div className="jarvis-briefing-header">
        <div className="jarvis-briefing-header-left">
          <div className="victor-briefing-avatar">⚡</div>
          <div>
            <div className="jarvis-briefing-greeting">{greeting}, {firstName}.</div>
            <div className="jarvis-briefing-date">
              {briefing?.date || new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
        <button className="jarvis-refresh-btn" onClick={onRefresh}>⟳ Refresh</button>
      </div>

      {briefing?.headline && (
        <div className="jarvis-headline">
          <span className="jarvis-headline-icon">◆</span>
          <span className="jarvis-headline-text">{briefing.headline}</span>
        </div>
      )}

      {/* Metrics — all clickable */}
      <div className="jarvis-metrics-row">
        <MetricCard label="New Leads" value={metrics?.newLeads ?? 0}
          sub={metrics?.highPriority > 0 ? `${metrics.highPriority} high priority` : 'none scored yet'}
          accent={metrics?.newLeads > 0} onClick={() => onNavigate?.('leads')} />
        <MetricCard label="Active Pipeline" value={metrics?.pipeline || '--'}
          sub={`${metrics?.opportunities ?? 0} opportunities`} onClick={() => onNavigate?.('pipeline')} />
        <MetricCard label="Do First" value={metrics?.urgentTasks ?? doFirst.length}
          sub={overdue.length > 0 ? `${overdue.length} overdue` : doFirst.length > 0 ? 'needs attention' : 'all clear'}
          warning={overdue.length > 0} accent={(metrics?.urgentTasks ?? doFirst.length) > 0} onClick={() => onNavigate?.('tasks')} />
        <MetricCard label="Focus Accounts" value={metrics?.focusAccounts ?? 0}
          sub="monitored" onClick={() => onNavigate?.('accounts')} />
      </div>

      {overdue.length > 0 && (
        <div className="jarvis-overdue-callout" onClick={() => onNavigate?.('tasks')}>
          <span className="jarvis-overdue-callout-icon">⚠</span>
          <span className="jarvis-overdue-callout-text">
            {overdue.length} overdue task{overdue.length > 1 ? 's' : ''} — past due date.
          </span>
          <span className="jarvis-overdue-callout-link">View Tasks →</span>
        </div>
      )}

      {briefing?.narrative && (
        <div className="jarvis-narrative">
          <div className="jarvis-narrative-label">{commandName} Assessment</div>
          <p className="jarvis-narrative-text">{briefing.narrative}</p>
          {briefing.focus && (
            <div className="jarvis-focus-line">
              <span className="jarvis-focus-icon">▶</span>
              <span className="jarvis-focus-text">{briefing.focus}</span>
            </div>
          )}
        </div>
      )}

      {briefing && !loading && (
        <div className="jarvis-feedback-row">
          {!ratingLogged ? (
            <>
              <span className="jarvis-feedback-label">Was this briefing on point?</span>
              {['Spot on', 'Mostly right', 'Off target'].map(rating => (
                <button key={rating}
                  className={`jarvis-feedback-btn ${briefingRating === rating ? 'jarvis-feedback-btn--active' : ''}`}
                  onClick={async () => {
                    setBriefingRating(rating);
                    try {
                      await apiFetch('/api/jarvis/log-feedback', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ date: new Date().toISOString().slice(0, 10), rating, headline: briefing.headline, focus: briefing.focus }),
                      });
                      setTimeout(() => setRatingLogged(true), 400);
                    } catch (e) { console.warn('Feedback log failed', e); }
                  }} type="button">
                  {rating === 'Spot on' ? '👍' : rating === 'Mostly right' ? '🤔' : '👎'} {rating}
                </button>
              ))}
            </>
          ) : (
            <span className="jarvis-feedback-saved">✓ Feedback logged</span>
          )}
        </div>
      )}

      <div className="jarvis-two-col">
        <div className="jarvis-panel">
          <div className="jarvis-panel-header jarvis-panel-header--decisions">⚠ Decisions Needed</div>
          <div className="jarvis-panel-body">
            {briefing?.decisionsNeeded?.length > 0
              ? briefing.decisionsNeeded.map((d, i) => (
                  <div key={i} className="jarvis-decision-item">
                    <span className="jarvis-decision-num">{i + 1}</span>
                    <span className="jarvis-decision-text">{d}</span>
                  </div>
                ))
              : <p className="jarvis-empty-state">No decisions pending.</p>}
          </div>
        </div>
        <div className="jarvis-panel">
          <div className="jarvis-panel-header jarvis-panel-header--actions">⚡ Victor Overnight</div>
          <div className="jarvis-panel-body">
            {(briefing?.victorActions || briefing?.jarvisActions)?.length > 0
              ? (briefing.victorActions || briefing.jarvisActions).map((a, i) => (
                  <div key={i} className="jarvis-action-item">
                    <span className="jarvis-action-check">✓</span>
                    <span className="jarvis-action-text">{a}</span>
                  </div>
                ))
              : <p className="jarvis-empty-state">Victor's overnight review is pending.</p>}
          </div>
        </div>
      </div>

      {/* Top Leads — clickable, show real title */}
      {newLeads?.length > 0 && (
        <div className="jarvis-section">
          <div className="jarvis-section-header">
            Top Leads for Review
            <button className="jarvis-section-link" onClick={() => onNavigate?.('leads')}>View all →</button>
          </div>
          <div className="jarvis-leads-list">
            {newLeads.slice(0, 4).map(lead => (
              <div key={lead.id}
                className="jarvis-lead-row jarvis-lead-row--clickable"
                onClick={() => setSelectedLead(lead)}
                title="Click to preview">
                <ScoreRing score={lead.score} />
                <div className="jarvis-lead-body">
                  <div className="jarvis-lead-hypothesis">
                    {getDisplayTitle(lead.leadTitle || lead.hypothesis)}
                  </div>
                  <div className="jarvis-lead-meta">
                    {lead.accountName && <span>{lead.accountName}</span>}
                    {lead.signalType  && <span> · {lead.signalType}</span>}
                  </div>
                </div>
                <PriorityBadge priority={lead.priority} />
                <span className="jarvis-lead-expand-hint">→</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {doFirst.length > 0 && (
        <div className="jarvis-section">
          <div className="jarvis-section-header">
            Do First
            <button className="jarvis-section-link" onClick={() => onNavigate?.('tasks')}>All tasks →</button>
          </div>
          {doFirst.slice(0, 3).map(t => (
            <div key={t.id} className={`jarvis-task-row${t.overdue ? ' jarvis-task-row--overdue' : ''}`}>
              <span className="jarvis-task-dot" />
              <span className="jarvis-task-name">{t.name}</span>
              {t.overdue && <span className="jarvis-overdue-badge">Overdue</span>}
            </div>
          ))}
        </div>
      )}

      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onOpenInLeads={(lead) => { setSelectedLead(null); onOpenLead(lead); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// TAB 2: Decision Queue — full expandable, no truncation, lead modal access
// ════════════════════════════════════════════════════════════════════════════════
function DecisionQueueTab({ newLeads, loading, onNavigate, onOpenLead }) {
  const [decisions, setDecisions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('jarvis_decisions') || '[]'); }
    catch { return []; }
  });
  const [expanded, setExpanded]     = useState({});
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    if (!newLeads?.length) return;
    setDecisions(prev => {
      const existingIds = new Set(prev.map(d => d.id));
      const fresh = newLeads
        .filter(l => !existingIds.has(l.id))
        .map(l => ({
          id: l.id, leadTitle: l.leadTitle || l.hypothesis || '',
          account: l.accountName || '', score: l.score, priority: l.priority,
          signalType: l.signalType || '', companyOverview: l.companyOverview || '',
          executiveTarget: l.executiveTarget || '', opportunityHypothesis: l.opportunityHypothesis || '',
          dealSize: l.dealSize || '', aiSignals: l.aiSignals || [],
          _lead: l, status: 'pending',
        }));
      const next = [...prev, ...fresh];
      localStorage.setItem('jarvis_decisions', JSON.stringify(next));
      return next;
    });
  }, [newLeads]);

  const decide = (id, action) => {
    setDecisions(prev => {
      const next = prev.map(d => d.id === id ? { ...d, status: action, decidedAt: new Date().toISOString() } : d);
      localStorage.setItem('jarvis_decisions', JSON.stringify(next));
      return next;
    });

    // Persist decision to Notion (fire and forget)
    const d = decisions.find(dec => dec.id === id);
    if (d) {
      apiFetch('/api/jarvis/log-decision', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leadId:      d.id,
          leadTitle:   d.leadTitle || d._lead?.leadTitle || '',
          accountId:   d._lead?.accountId || null,
          accountName: d.account || d._lead?.accountName || '',
          decision:    action,
          score:       d.score,
          priority:    d.priority,
          dealSize:    d.dealSize || d._lead?.dealSize || '',
        }),
      }).catch(() => {});
    }
  };

  const clearActioned = () => {
    setDecisions(prev => {
      const next = prev.filter(d => d.status === 'pending');
      localStorage.setItem('jarvis_decisions', JSON.stringify(next));
      return next;
    });
  };

  const pending  = decisions.filter(d => d.status === 'pending');
  const actioned = decisions.filter(d => d.status !== 'pending');

  if (loading) return <div className="jarvis-loading"><JarvisAvatar size={48} pulse /><p className="jarvis-loading-text">Loading...</p></div>;

  return (
    <div className="jarvis-tab-content">
      <JarvisSays
        message={pending.length > 0 ? `${pending.length} item${pending.length > 1 ? 's' : ''} need your decision.` : actioned.length > 0 ? 'All decisions actioned.' : 'Queue is clear.'}
        sub="Expand to read the full brief. Click 'Preview Lead' for the full card."
      />

      {pending.length > 0 && (
        <div className="jarvis-section">
          <div className="jarvis-section-header">Pending ({pending.length})</div>
          {pending.map(d => {
            const isExp    = expanded[d.id];
            const hasDetail = d.companyOverview || d.executiveTarget || d.opportunityHypothesis;

            return (
              <div key={d.id} className={`jarvis-decision-card${isExp ? ' jarvis-decision-card--expanded' : ''}`}>
                <div
                  className="jarvis-decision-card-top jarvis-decision-card-top--clickable"
                  onClick={() => hasDetail && setExpanded(p => ({ ...p, [d.id]: !isExp }))}
                >
                  <ScoreRing score={d.score} />
                  <div className="jarvis-decision-card-body">
                    <div className="jarvis-decision-card-title">{getDisplayTitle(d.leadTitle)}</div>
                    <div className="jarvis-decision-card-account">
                      {d.account}{d.dealSize && <span className="jarvis-decision-deal-size"> · {d.dealSize}</span>}
                    </div>
                    {!isExp && (d.opportunityHypothesis || d.companyOverview) && (
                      <div className="jarvis-decision-card-preview">
                        {(d.opportunityHypothesis || d.companyOverview).slice(0, 160)}…
                      </div>
                    )}
                  </div>
                  <div className="jarvis-decision-card-right">
                    <PriorityBadge priority={d.priority} />
                    {hasDetail && <span className="jarvis-decision-expand-btn">{isExp ? '▲' : '▼'}</span>}
                  </div>
                </div>

                {isExp && (
                  <div className="jarvis-decision-card-detail">
                    {d.signalType && (
                      <div className="jarvis-decision-detail-block">
                        <span className="jarvis-decision-detail-label">Signal</span>
                        <span className="jarvis-decision-signal-tag">{d.signalType}</span>
                      </div>
                    )}
                    {d.aiSignals?.length > 0 && (
                      <div className="jarvis-decision-detail-block">
                        <span className="jarvis-decision-detail-label">AI Signals</span>
                        <div className="jarvis-decision-signals-row">
                          {d.aiSignals.map((s, i) => <span key={i} className="jarvis-decision-signal-tag">{s}</span>)}
                        </div>
                      </div>
                    )}
                    {d.opportunityHypothesis && (
                      <div className="jarvis-decision-detail-block">
                        <span className="jarvis-decision-detail-label">Opportunity Hypothesis</span>
                        <p className="jarvis-decision-detail-text">{d.opportunityHypothesis}</p>
                      </div>
                    )}
                    {d.companyOverview && (
                      <div className="jarvis-decision-detail-block">
                        <span className="jarvis-decision-detail-label">Company Context</span>
                        <p className="jarvis-decision-detail-text">{d.companyOverview}</p>
                      </div>
                    )}
                    {d.executiveTarget && (
                      <div className="jarvis-decision-detail-block">
                        <span className="jarvis-decision-detail-label">Target Executive</span>
                        <p className="jarvis-decision-detail-text">{d.executiveTarget}</p>
                      </div>
                    )}
                    {d._lead && (
                      <button className="jarvis-btn jarvis-btn--outline jarvis-decision-open-btn"
                        onClick={() => setSelectedLead(d._lead)} type="button">
                        📋 Preview full lead card
                      </button>
                    )}
                  </div>
                )}

                <div className="jarvis-decision-card-actions">
                  <button className="jarvis-btn jarvis-btn--approve" onClick={() => decide(d.id, 'approved')}>✓ Approve</button>
                  <button className="jarvis-btn jarvis-btn--defer"   onClick={() => decide(d.id, 'deferred')}>↺ Defer</button>
                  <button className="jarvis-btn jarvis-btn--pass"    onClick={() => decide(d.id, 'passed')}>✕ Pass</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {actioned.length > 0 && (
        <div className="jarvis-section">
          <div className="jarvis-section-header">Actioned ({actioned.length})<button className="jarvis-section-link" onClick={clearActioned}>Clear</button></div>
          {actioned.map(d => (
            <div key={d.id} className={`jarvis-decision-card jarvis-decision-card--${d.status}`}>
              <div className="jarvis-decision-card-top">
                <div className="jarvis-decision-card-body">
                  <div className="jarvis-decision-card-title">{getDisplayTitle(d.leadTitle)}</div>
                  <div className="jarvis-decision-card-account">{d.account}</div>
                </div>
                <span className={`jarvis-status-tag jarvis-status-tag--${d.status}`}>
                  {d.status === 'approved' ? '✓ Approved' : d.status === 'deferred' ? '↺ Deferred' : '✕ Passed'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {decisions.length === 0 && (
        <div className="jarvis-empty-center">
          <div className="jarvis-empty-icon">✓</div>
          <div className="jarvis-empty-title">Queue is clear</div>
          <div className="jarvis-empty-sub">Selma will populate this overnight.</div>
          <button className="jarvis-btn jarvis-btn--secondary" onClick={() => onNavigate?.('research')}>Open Selma's Workspace →</button>
        </div>
      )}

      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onOpenInLeads={(lead) => { setSelectedLead(null); onOpenLead(lead); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// TAB 3 & 4 (unchanged logic, same as before)
// ════════════════════════════════════════════════════════════════════════════════
function OrchestrationLogTab({ newLeads, recentSignals, metrics, generatedAt }) {
  const { commandName, researchName } = useProfile();
  const now = generatedAt ? new Date(generatedAt) : new Date();
  const fmt = (d) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const entries = [
    { time: fmt(now), agent: commandName, type: 'briefing', message: `Morning briefing generated. ${metrics?.newLeads || 0} new leads analyzed.` },
    { time: fmt(new Date(now - 2*60000)), agent: commandName, type: 'scoring', message: `Scored ${metrics?.newLeads || 0} leads. ${metrics?.highPriority || 0} High priority.` },
    ...(newLeads||[]).slice(0,4).map((l,i)=>({ time: fmt(new Date(now-(5+i*4)*60000)), agent: researchName, type: 'research', message: `"${getDisplayTitle(l.leadTitle||l.hypothesis||'').slice(0,90)}"${l.accountName?' — '+l.accountName:''}.` })),
    ...(recentSignals||[]).slice(0,2).map(s=>({ time: fmt(new Date(now-35*60000)), agent: researchName, type: 'signal', message: `Signal: [${s.type||'Market'}] ${s.signal?.slice(0,80)}` })),
    { time: '01:00 AM', agent: commandName, type: 'system', message: `Overnight batch initiated.` },
  ];
  const cfg = { briefing:{icon:'◆',color:'#06b6d4'}, scoring:{icon:'◎',color:'var(--dm-blue-mid)'}, research:{icon:'🔭',color:'var(--dm-indigo-soft)'}, signal:{icon:'⚡',color:'#f59e0b'}, system:{icon:'●',color:'var(--dm-grey-500)'} };
  return (
    <div className="jarvis-tab-content">
      <JarvisSays message="Complete record of overnight activity." sub="Every AI action timestamped." />
      <div className="jarvis-log">
        {entries.map((e,i)=>{const c=cfg[e.type]||cfg.system; return (
          <div key={i} className="jarvis-log-entry">
            <div className="jarvis-log-time">{e.time}</div>
            <div className="jarvis-log-icon-col">
              <div className="jarvis-log-dot" style={{background:c.color}}><span style={{fontSize:'0.6rem'}}>{c.icon}</span></div>
              {i<entries.length-1&&<div className="jarvis-log-line"/>}
            </div>
            <div className="jarvis-log-body">
              <span className="jarvis-log-agent" style={{color:c.color}}>{e.agent}</span>
              <span className="jarvis-log-msg">{e.message}</span>
            </div>
          </div>
        );})}
      </div>
    </div>
  );
}

function AITeamStatusTab({ metrics, onNavigate }) {
  const { commandName, researchName, strategyName, commsName } = useProfile();
  const sq = (()=>{ try{return JSON.parse(localStorage.getItem('selma_queue')||'[]');}catch{return[];} })();
  const sr = (()=>{ try{return JSON.parse(localStorage.getItem('selma_review_queue')||'[]');}catch{return[];} })();
  const tonight=sq.filter(j=>j.status==='tonight').length, active=sq.filter(j=>j.status==='active').length, review=sr.length;
  const personas = [
    { name:commandName, role:'Command & Orchestration', icon:'J', color:'#06b6d4', statusText:'Online — briefing generated', statusType:'online', detail:`${metrics?.newLeads||0} leads scored.`, nav:null, built:true },
    { name:researchName, role:'Intelligence & Research', icon:'🔭', color:'var(--dm-indigo-soft)', statusText:active>0?`Running ${active}`:(tonight>0?`${tonight} tonight`:(review>0?`${review} ready`:'Standing by')), statusType:active>0?'active':tonight>0?'queued':review>0?'review':'idle', detail:`${tonight} tonight, ${active} active, ${review} ready.`, nav:'research', built:true },
    { name:strategyName, role:'Deal Strategy', icon:'V', color:'var(--dm-blue-deep)', statusText:'Coming next sprint', statusType:'planned', detail:'MEDDIC scoring, pursuit strategy, competitive positioning.', nav:null, built:false },
    { name:commsName, role:'Communications', icon:'C', color:'var(--dm-purple-vivid)', statusText:'Coming after Victor', statusType:'planned', detail:'Outreach emails, proposals, and LinkedIn from Selma intel.', nav:null, built:false },
  ];
  const dotColors = { online:'var(--dm-green-soft)', active:'#f59e0b', queued:'var(--dm-indigo-soft)', review:'var(--dm-blue-mid)', idle:'var(--dm-grey-500)', planned:'var(--dm-grey-400)' };
  return (
    <div className="jarvis-tab-content">
      <JarvisSays message="Current status of all AI team members." sub="Click any active persona to open their workspace." />
      <div className="jarvis-team-grid">
        {personas.map(p=>(
          <div key={p.name} className={`jarvis-team-card${p.nav?' jarvis-team-card--clickable':''}${!p.built?' jarvis-team-card--planned':''}`}
            style={{borderLeftColor:p.color}} onClick={()=>p.nav&&onNavigate?.(p.nav)}>
            <div className="jarvis-team-card-top">
              <div className="jarvis-team-icon" style={{background:p.color+'22'}}>
                {p.icon.length<=2?<span className="jarvis-team-icon-letter" style={{color:p.color}}>{p.icon}</span>:<span>{p.icon}</span>}
              </div>
              <div className="jarvis-team-info"><div className="jarvis-team-name">{p.name}</div><div className="jarvis-team-role">{p.role}</div></div>
              <div className="jarvis-team-dot" style={{background:dotColors[p.statusType]||'var(--dm-grey-500)'}}/>
            </div>
            <div className="jarvis-team-status" style={{color:dotColors[p.statusType]||'var(--dm-grey-500)'}}>{p.statusText}</div>
            <div className="jarvis-team-detail">{p.detail}</div>
            {p.nav&&<div className="jarvis-team-open">Open workspace →</div>}
            {!p.built&&<div className="jarvis-team-planned-badge">Planned — Sprint 3</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// MAIN JarvisPage
// ════════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: 'briefing',  label: 'Victor Briefing',  icon: '⚡' },
  { id: 'decisions', label: 'Decision Queue',    icon: '⚖' },
  { id: 'log',       label: 'Orchestration Log', icon: '◎' },
  { id: 'team',      label: 'AI Team Status',    icon: '▣' },
];

const CACHE_KEY     = 'jarvis_briefing_cache';
const CACHE_MAX_AGE = 30 * 60 * 1000;

export default function JarvisPage({ onNavigate, onOpenLead }) {
  const { commandName } = useProfile();
  const [activeTab,      setActiveTab]      = useState('briefing');
  const [briefing,       setBriefing]       = useState(null);
  const [metrics,        setMetrics]        = useState(null);
  const [newLeads,       setNewLeads]       = useState([]);
  const [urgentTasks,    setUrgentTasks]    = useState([]);
  const [recentSignals,  setRecentSignals]  = useState([]);
  const [generatedAt,    setGeneratedAt]    = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [lastGenerated,  setLastGenerated]  = useState(null);
  const [briefingRating, setBriefingRating] = useState(null);
  const [ratingLogged,   setRatingLogged]   = useState(false);

  const applyData = useCallback((data) => {
    setBriefing(data.briefing);
    setMetrics(data.metrics);
    setNewLeads(data.newLeads || []);
    setUrgentTasks(data.urgentTasks || []);
    setRecentSignals(data.recentSignals || []);
    setGeneratedAt(data.generatedAt || null);
    setLastGenerated(data._cachedAt
      ? `${new Date(data._cachedAt).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})} (cached)`
      : new Date().toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}));
  }, []);

  const loadBriefing = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
        if (cached && (Date.now() - cached._cachedAt) < CACHE_MAX_AGE) {
          applyData(cached); setLoading(false); setBriefingRating(null); setRatingLogged(false); return;
        }
      } catch {}
    }
    setLoading(true); setError(null);
    try {
      const res  = await apiFetch('/api/jarvis/briefing');
      const data = await res.json();
      if (data.success) {
        const toCache = { ...data, _cachedAt: Date.now() };
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(toCache)); } catch {}
        applyData(toCache); setBriefingRating(null); setRatingLogged(false);
      } else { setError(data.error || 'Briefing unavailable'); }
    } catch (err) { setError('Could not reach the briefing service.'); }
    finally { setLoading(false); }
  }, [applyData]);

  useEffect(() => { loadBriefing(false); }, [loadBriefing]);

  // Navigate to Leads page and open specific lead card
  const handleOpenLead = useCallback((lead) => {
    if (onOpenLead) {
      onOpenLead(lead.id, lead);  // App.jsx receives this: navigate + set openLeadId
    } else if (onNavigate) {
      onNavigate('leads');
    }
  }, [onOpenLead, onNavigate]);

  const decisionCount = newLeads.length;

  return (
    <div className="jarvis-root">
      <div className="jarvis-header">
        <div className="jarvis-header-left">
          <JarvisAvatar size={44} pulse={loading} />
          <div>
            <div className="jarvis-header-title">{commandName} Command Studio</div>
            <div className="jarvis-header-sub">
              {loading ? 'Generating briefing...' : lastGenerated ? `Briefing: ${lastGenerated}` : 'Ready'}
            </div>
          </div>
        </div>
        <div className="jarvis-header-right">
          {!loading && !error && <div className="jarvis-online-badge"><div className="jarvis-online-dot"/>Online</div>}
        </div>
      </div>

      <div className="jarvis-tabs">
        {TABS.map(tab => {
          const badge = tab.id === 'decisions' && decisionCount > 0 ? decisionCount : null;
          return (
            <button key={tab.id} className={`jarvis-tab${activeTab===tab.id?' jarvis-tab--active':''}`} onClick={()=>setActiveTab(tab.id)}>
              <span className="jarvis-tab-icon">{tab.icon}</span>
              {tab.label}
              {badge && <span className="jarvis-tab-badge">{badge}</span>}
            </button>
          );
        })}
      </div>

      {error && <div className="jarvis-error"><span>⚠</span> {error}<button className="jarvis-error-retry" onClick={()=>loadBriefing(true)}>Retry</button></div>}

      {activeTab==='briefing'  && <MorningBriefingTab briefing={briefing} metrics={metrics} newLeads={newLeads} urgentTasks={urgentTasks} loading={loading} onRefresh={()=>loadBriefing(true)} onNavigate={onNavigate} onOpenLead={handleOpenLead} briefingRating={briefingRating} setBriefingRating={setBriefingRating} ratingLogged={ratingLogged} setRatingLogged={setRatingLogged} />}
      {activeTab==='decisions' && <DecisionQueueTab newLeads={newLeads} loading={loading} onNavigate={onNavigate} onOpenLead={handleOpenLead} />}
      {activeTab==='log'       && <OrchestrationLogTab newLeads={newLeads} recentSignals={recentSignals} metrics={metrics} generatedAt={generatedAt} />}
      {activeTab==='team'      && <AITeamStatusTab metrics={metrics} onNavigate={onNavigate} />}
    </div>
  );
}
